import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  signInAnonymously,
} from 'firebase/auth';
import { auth } from '../firebase';
import { useSessionUser } from '@/hooks/useSessionUser';
import { SteamUser } from '@/types/user';
import EdgeButton from '@/components/_buttons/EdgeButton';
import { AudioProvider } from './AudioContext';
import { useTranslation } from 'next-i18next';
import { BASEURL, SupportedLanguage } from '@/settings/endpoint';
import { axios } from '@/libs/api';

interface AuthContextProps {
  user: SteamUser | null;
  token: string | null;
  loading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n, t } = useTranslation();
  const { user, login, getSessionUser, setUser } = useSessionUser();
  const [idToken, setIdToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const handleReload = () => {
    window.location.reload();
  };

  const _login = async (language: string) => {
    if (!i18n) return;
    try {
      // Steam/Apple OAuthリダイレクト後、?name= パラメータをlocalStorageに保存
      const urlParams = new URLSearchParams(window.location.search);
      const nameFromUrl = urlParams.get('name');
      if (nameFromUrl) {
        localStorage.setItem('userName', nameFromUrl);
        localStorage.setItem('launched', 'true');
      }

      const user = await signInAnonymously(auth);
      const token = await user.user?.getIdToken();
      setIdToken(token);
      if (!token) {
        throw new Error('Firebase token is missing.');
      }
      const storedUserName = localStorage.getItem('userName');
      const userName = storedUserName || t('common.defaultUsername');

      const isJapanese = language === 'ja';
      const baseUrl = BASEURL.jp.appUrl || process.env.NEXT_PUBLIC_APP_HOST_JA;
      const i18nLanguage = isJapanese ? 'jp' : 'us';

      if (baseUrl) {
        axios.defaults.baseURL = baseUrl;
      }
      if (i18n && typeof i18n.changeLanguage === 'function') {
        await i18n.changeLanguage(i18nLanguage);
      }
      const sessionUser = await getSessionUser();
      if (sessionUser) {
        // セッションが既に存在する場合（Steam/Apple連携後など）、firebase-loginをスキップ
        setUser(sessionUser);
      } else {
        // セッションが存在しない場合、初回起動時も含めてfirebase-loginで認証
        await login(userName, token, i18nLanguage as SupportedLanguage);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('ログインに失敗しました。');
    }
    setLoading(false);
  };

  useEffect(() => {
    _login('ja');
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token: idToken,
        loading,
      }}
    >
      {errorMessage ? (
        <AudioProvider>
          <div
            className="fixed left-[50%] flex flex-col text-white items-center gap-4 justify-center"
            style={{ top: '50%', transform: 'translate(-50%, -50%' }}
          >
            <div className="text-2xl">{errorMessage}</div>
            <EdgeButton onClick={handleReload}>ページを更新</EdgeButton>
          </div>
        </AudioProvider>
      ) : (
        <> {children}</>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
